import { useInvoiceState } from '@/modules/billing/review/editDraft/useInvoiceState';
import { useGroupActions } from '@/modules/billing/review/editDraft/useGroupActions';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { calculateEntryAmount } from '@/services/utils';
import axios from 'axios';
export function useInvoiceActions() {
  const invoiceState = useInvoiceState();
  const {
    handleError
  } = useSnackbarHandler();
  const {
    findOrCreateGroup,
    moveLineItemsToGroup,
    unCheckAll,
    makeNonBillableLineItem
  } = useGroupActions();

  // common functions
  function processSelectedItems(processItem) {
    invoiceState.groups.forEach(g => {
      g.lineItems.filter(item => item.checked).forEach(processItem);
    });
  }

  // make non billable
  const makeGroupNonBillable = () => {
    if (!hasGroupLineItemSelected()) return;
    processSelectedItems(lineItem => makeNonBillableLineItem(lineItem));
    unCheckAll();
  };

  // write down to zero
  const writeDownGroupToZero = () => {
    if (!hasGroupLineItemSelected()) return;
    processSelectedItems(lineItem => writeDownLineItemToZero(lineItem));
    updateLineItemsTotals();
    unCheckAll();
  };

  // save for later
  const saveLineGroupForLater = () => {
    if (!hasGroupLineItemSelected()) return;
    processSelectedItems(lineItem => invoiceState.removeLineItem(lineItem, 3)); //lineItemStatus.savedForLater
    unCheckAll();
  };

  // change matter modal
  const changeMatterForGroup = async ({
    matterId,
    timeSheetId
  }) => {
    if (!hasGroupLineItemSelected()) return;
    processSelectedItems(lineItem => changeMatterForLineItem({
      lineItem,
      matterId,
      timeSheetId
    }));
    unCheckAll(false);
  };
  const writeDownLineItemToZero = lineItem => {
    lineItem.amount = 0;
    lineItem.hours = 0;
    lineItem.status = 'writeDown';
    lineItem.entries.forEach(e => {
      e.writedown = e.hours;
      e.billedHours = 0;
    });
    updateInvoiceGroup(lineItem);
    unCheckAll(false);
  };

  // Bulk version: write down multiple line items to zero
  const writeDownMultipleLineItemsToZero = lineItems => {
    lineItems.forEach(lineItem => {
      writeDownLineItemToZero(lineItem);
    });
  };
  const findOriginalGroup = lineItem => {
    if (lineItem.charges) {
      let fixedFeeGroup = invoiceState.groups.find(x => x.lineItems.some(li => li.charges && li.charges.some(e => e.id === lineItem.charges[0].id)));
      return fixedFeeGroup;
    } else if (lineItem.entries) {
      let entryGroup = invoiceState.groups.find(x => x.lineItems.some(li => li.entries && li.entries.some(e => e.id === lineItem.entries[0].id)));
      return entryGroup;
    } else if (lineItem.expenses) {
      let expenseGroup = invoiceState.groups.find(x => x.lineItems.some(li => li.expenses && li.expenses.some(e => e.id === lineItem.expenses[0].id)));
      return expenseGroup;
    }
  };
  const changeMatterForLineItem = async ({
    lineItem,
    matterId,
    timeSheetId
  }) => {
    let originalGroup = findOriginalGroup(lineItem);
    if (!originalGroup) {
      handleError('Could not find original group for this line item');
      return;
    }
    let originalMatter = invoiceState.matters.find(x => x.id === originalGroup.matter.id) || (invoiceState.matter?.id === originalGroup.matter.id ? invoiceState.matter : null);
    if (!originalMatter) {
      handleError('Could not find original matter for this line item');
      return;
    }
    const status = timeSheetId || matterId !== originalMatter.id ? 'updated' : 'savedForLater';
    lineItem.status = status;

    // Update the line item's matter reference to the new matter
    const newMatter = invoiceState.matters.find(x => x.id === matterId);
    if (newMatter) {
      lineItem.matter = newMatter;
    }
    if (lineItem.entries) {
      for (let entry of lineItem.entries) {
        entry.matterId = matterId;
        entry.timeSheetId = timeSheetId;
        entry.chargeId = null;
        await axios.get(`/matters/${entry.matterId}/users/${entry.account.id}/rate`).then(response => {
          var data = response.data;
          entry.rate = {
            ...data.rate
          };
          entry.rate.edited = true;
          lineItem.rate = entry.billingType === 'billable' ? entry.rate : {
            amount: 0
          };
          lineItem.amount = entry.billingType === 'billable' ? calculateEntryAmount(lineItem.rate.amount, lineItem.hours) : 0;
        }).catch(error => {
          handleError(error);
        });
      }
    } else if (lineItem.expenses) {
      lineItem.expenses.forEach(e => {
        e.matterId = matterId;
        e.timeSheetId = timeSheetId;
      });
    } else if (lineItem.charges) {
      const isSameInvoice = invoiceState.id === timeSheetId && invoiceState.matters?.length > 1;
      const chargeMoveTargetMatter = isSameInvoice ? invoiceState.matters.find(x => x.id === matterId) : null;
      const chargeIds = new Set(lineItem.charges.map(c => c.id));
      lineItem.charges.forEach(c => {
        c.matterId = matterId;
        c.timeSheetId = timeSheetId;
      });

      // Collect associated entry line items before modifying groups
      // (deduplicate so each detail line item is processed only once)
      const processedIds = new Set();
      const associatedEntryLineItems = [];
      invoiceState.groups.forEach(group => {
        group.lineItems.forEach(li => {
          if (li.entries && !processedIds.has(li.id)) {
            if (li.entries.some(entry => entry.chargeId && chargeIds.has(entry.chargeId))) {
              li.entries.forEach(entry => {
                entry.matterId = matterId;
                entry.timeSheetId = timeSheetId;
              });
              li.status = status;
              processedIds.add(li.id);
              associatedEntryLineItems.push(li);
            }
          }
        });
      });

      // Move or remove associated entries based on move type
      associatedEntryLineItems.forEach(li => {
        if (isSameInvoice && chargeMoveTargetMatter) {
          // Same invoice: move entries to new matter's fixedFeeEntries group
          invoiceState.removeLineItem(li, status, true);
          const targetGroup = findOrCreateGroup('fixedFeeEntries', chargeMoveTargetMatter);
          moveLineItemsToGroup([li], targetGroup);
        } else {
          // Different invoice or save for later: remove entirely
          invoiceState.removeLineItem(li);
        }
      });
    }
    if (invoiceState.id === timeSheetId && invoiceState.matters?.length > 1) {
      // moving onto same timesheet
      var moveToMatter = invoiceState.matters.find(x => x.id === matterId);
      if (moveToMatter) {
        invoiceState.removeLineItem(lineItem, status, true);
        var groupToMoveTo = findOrCreateGroup(originalGroup.type, moveToMatter);
        lineItem.description = lineItem.description.replace(originalMatter.name, moveToMatter.name);
        moveLineItemsToGroup([lineItem], groupToMoveTo);
      }
    } else {
      // moving onto different timesheet or saving for later
      invoiceState.removeLineItem(lineItem, status);
    }
  };
  const updateInvoiceGroup = lineItem => {
    invoiceState.groups.forEach((group, i) => {
      group.lineItems.forEach((li, j) => {
        if (li.id !== lineItem?.id) return;
        invoiceState.groups[i].lineItems[j] = lineItem;
      });
      group.lineItems.sort((a, b) => {
        return true ? new Date(b.date) - new Date(a.date) : new Date(a.date) - new Date(b.date);
      });
    });
    invoiceState.updateLineItemsTotals(lineItem);
  };

  // replace this with invoice store
  const hasGroupLineItemSelected = () => {
    return invoiceState.groups.some(group => {
      return group.lineItems.some(item => item.checked);
    });
  };
  return {
    writeDownLineItemToZero,
    changeMatterForLineItem,
    changeMatterForGroup,
    makeGroupNonBillable,
    writeDownGroupToZero,
    saveLineGroupForLater,
    writeDownMultipleLineItemsToZero,
    findOriginalGroup
  };
}